/************************************************************
 * Description:
 * Streamlined typing animation function that cycles through
 * an array of strings, typing them out with optional markup
 * handling, pausing, and backspacing. Various speeds and
 * behaviors can easily be configured via an options object.
 ************************************************************/
import i18next from "i18next";
import {remark} from "remark";
import remarkGfm from "remark-gfm";
import remarkHtml from "remark-html";
import stripMarkdown from "strip-markdown";
import {unified} from "unified";

import {
    AssistantAnswer,
    AssistantReasoningContent,
    GPTModel,
    SourceInfo,
    RelatedSourceInfo,
    Usage,
    Artifact,
    DirectSearchResult,
} from "../../types";
import {getArtifactTitle} from "./components/artifacts/title";
import {
    normalizeMathDelimiters,
    outsideCode,
} from "./components/stream-parser/utils";

const escapeHtml = (s: string) =>
    s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

/**
 * Renders an artifact into clipboard HTML. Returns "" for types with no copy representation.
 * Generalized per type so new artifact types can be added alongside search_results.
 */
const artifactToClipboardHtml = (artifact: Artifact): string => {
    if (artifact.type === "search_results") {
        const results =
            (artifact.payload?.results as DirectSearchResult[]) ?? [];
        if (results.length === 0) return "";
        const items = results
            .map((r) => {
                const desc = r.description ?? r.snippet ?? "";
                const descHtml = desc ? ` - ${escapeHtml(desc)}` : "";
                return `<li><a href="${escapeHtml(
                    r.url
                )}" target="_blank"><strong>${escapeHtml(
                    r.title
                )}</strong></a>${descHtml}</li>`;
            })
            .join("\n");
        return `<section><h3>${escapeHtml(
            getArtifactTitle(artifact)
        )}</h3><ul>${items}</ul></section>`;
    }
    return "";
};

/**
 * Renders an artifact into clipboard plain text. Returns "" for types with no copy representation.
 */
const artifactToClipboardText = (artifact: Artifact): string => {
    if (artifact.type === "search_results") {
        const results =
            (artifact.payload?.results as DirectSearchResult[]) ?? [];
        if (results.length === 0) return "";
        const items = results
            .map((r) => {
                const desc = r.description ?? r.snippet ?? "";
                return `${r.title}${desc ? ` - ${desc}` : ""} (${r.url})`;
            })
            .join("\n");
        return `\n\n${getArtifactTitle(artifact)}\n${items}`;
    }
    return "";
};

/** Signature for a simple callback that receives updated text as it changes. */
export type TextChangedCallback = (
    updatedText: string,
    isFinished: boolean
) => void;

/** Options for controlling the type animation behavior. */
export interface TypeAnimationOptions {
    typingSpeed?: number;
    backspaceSpeed?: number;
    holdSpeed?: number;
    initialHoldSpeed?: number;
    waitSpeed?: number;
    loop?: boolean;
    backspaceOnFinish?: boolean;
    preserveMarkup?: boolean; // If false, markup is typed as plain text.
}

/**
 * Animate typing, holding, and backspacing over a single string.
 * @param text The string to animate.
 * @param onTextChanged Callback invoked whenever the animated text changes.
 * @param userOptions Optional configuration for speeds and loop behavior.
 * Loop is true by default. If false, the animation will stop after the first message and preserve the text.
 * @returns A promise that resolves when the animation is complete.
 */
export async function typeAnimatedText(
    text: string,
    onTextChanged: TextChangedCallback,
    userOptions?: TypeAnimationOptions
): Promise<void> {
    const {
        typingSpeed = 100,
        backspaceSpeed = 50,
        holdSpeed = 1000,
        waitSpeed = 2000,
        loop = true,
        preserveMarkup = true,
        backspaceOnFinish = true,
    } = userOptions || {};

    let animatedText = "";

    /**
     * Update the current text and call the user-supplied callback.
     */
    const updateText = (newText: string) => {
        animatedText = newText;
        onTextChanged(
            animatedText,
            !backspaceOnFinish && text.length === animatedText.length
        );
    };

    /**
     * Type out a string with optional markup handling.
     */
    const typeString = async (text: string) => {
        let tempMarkup = "";
        let insideMarkup = false;

        for (const char of text) {
            if (preserveMarkup && char === "<") {
                // If encountering a markup tag beginning
                insideMarkup = true;
                if (tempMarkup) {
                    // Append buffered text before opening the tag
                    updateText(animatedText + tempMarkup);
                    tempMarkup = "";
                }
                tempMarkup += char;
            } else if (preserveMarkup && insideMarkup && char === ">") {
                // Close the tag
                insideMarkup = false;
                tempMarkup += char;
                updateText(animatedText + tempMarkup);
                tempMarkup = "";
            } else if (preserveMarkup && insideMarkup) {
                // Continue inside the tag
                tempMarkup += char;
            } else {
                // Normal typing (or plain typing if preserveMarkup === false)
                updateText(animatedText + char);
                await sleep(typingSpeed);
            }
            // Small delay for re-render pacing (optional).
            await sleep(1);
        }

        // If something remains in tempMarkup after the loop ends, append it.
        if (tempMarkup) {
            updateText(animatedText + tempMarkup);
        }
    };

    /**
     * Backspace the entire animated text (including markup if preserveMarkup is enabled).
     */
    const backspaceString = async () => {
        if (!backspaceOnFinish) {
            return;
        }
        while (animatedText.length > 0) {
            // Handle markup backspacing
            if (preserveMarkup && animatedText.endsWith(">")) {
                const lastTagStart = animatedText.lastIndexOf("<");
                if (lastTagStart !== -1) {
                    animatedText = animatedText.slice(0, lastTagStart);
                    updateText(animatedText);
                    await sleep(backspaceSpeed);
                    continue;
                }
            }
            // Normal backspacing
            animatedText = animatedText.slice(0, -1);
            updateText(animatedText);
            await sleep(backspaceSpeed);
        }
    };

    /**
     * Main driver that loops through each message in sequence.
     */
    const runAnimation = async () => {
        do {
            await typeString(text);
            await sleep(holdSpeed);
            await backspaceString();
            await sleep(waitSpeed);
        } while (
            loop &&
            !(!backspaceOnFinish && animatedText.length === text.length)
        );
    };

    // Begin the animation
    await runAnimation();
}

/**
 * Animate typing, holding, and backspacing over a list of strings.
 * @param messages Array of strings to loop through.
 * @param onTextChanged Callback invoked whenever the animated text changes.
 * @param userOptions Optional configuration for speeds and loop behavior.
 */
export async function typeAnimatedTexts(
    messages: string[],
    onTextChanged: TextChangedCallback,
    userOptions?: TypeAnimationOptions
): Promise<void> {
    // Handle empty messages array
    if (!messages || messages.length === 0) {
        onTextChanged("", true); // Indicate finished state immediately
        return;
    }

    const {
        typingSpeed = 100,
        backspaceSpeed = 50,
        holdSpeed = 1000,
        initialHoldSpeed = 3000,
        waitSpeed = 2000,
        loop = true,
        backspaceOnFinish = true,
        preserveMarkup = true,
    } = userOptions || {};

    let animatedText = "";
    // let index = 0; // Removed, index logic handled differently below

    /**
     * Update the current text and call the user-supplied callback.
     */
    const updateText = (newText: string, isFinished = false) => {
        animatedText = newText;
        // Pass false for isFinished during animation, true only at the very end if not looping
        onTextChanged(animatedText, isFinished);
    };

    /**
     * Type out a string with optional markup handling.
     */
    const typeString = async (text: string) => {
        let tempMarkup = "";
        let insideMarkup = false;

        for (const char of text) {
            if (preserveMarkup && char === "<") {
                // If encountering a markup tag beginning
                insideMarkup = true;
                if (tempMarkup) {
                    // Append buffered text before opening the tag
                    updateText(animatedText + tempMarkup);
                    tempMarkup = "";
                }
                tempMarkup += char;
            } else if (preserveMarkup && insideMarkup && char === ">") {
                // Close the tag
                insideMarkup = false;
                tempMarkup += char;
                updateText(animatedText + tempMarkup);
                tempMarkup = "";
            } else if (preserveMarkup && insideMarkup) {
                // Continue inside the tag
                tempMarkup += char;
            } else {
                // Normal typing (or plain typing if preserveMarkup === false)
                updateText(animatedText + char);
                await sleep(typingSpeed);
            }
            // Small delay for re-render pacing (optional).
            await sleep(1);
        }

        // If something remains in tempMarkup after the loop ends, append it.
        if (tempMarkup) {
            updateText(animatedText + tempMarkup);
        }
    };

    /**
     * Backspace the entire animated text (including markup if preserveMarkup is enabled).
     */
    const backspaceString = async () => {
        while (animatedText.length > 0) {
            // Handle markup backspacing
            if (preserveMarkup && animatedText.endsWith(">")) {
                const lastTagStart = animatedText.lastIndexOf("<");
                if (lastTagStart !== -1) {
                    animatedText = animatedText.slice(0, lastTagStart);
                    updateText(animatedText);
                    await sleep(backspaceSpeed);
                    continue;
                }
            }
            // Normal backspacing
            animatedText = animatedText.slice(0, -1);
            updateText(animatedText);
            await sleep(backspaceSpeed);
        }
    };

    // --- Start of Modified Logic ---

    // 1. Initial Display & Wait
    updateText(messages[0]); // Show the first message instantly
    await sleep(initialHoldSpeed);

    // 2. Initial Backspace & Wait
    await backspaceString();
    await sleep(waitSpeed);

    // 3. Animation Loop
    let currentMessageIndex = 1; // Start loop processing from the second message

    // Check if there's only one message and no loop
    if (messages.length === 1 && !loop) {
        if (!backspaceOnFinish) {
            // Don't backspace, just mark as finished with the message still displayed
            updateText(messages[0], true);
        } else {
            updateText("", true); // Already backspaced, mark as finished
        }
        return; // Nothing more to do
    }

    // Main loop driver
    const runAnimationLoop = async () => {
        do {
            // Handle index wrap-around or termination
            if (currentMessageIndex >= messages.length) {
                if (loop) {
                    currentMessageIndex = 0; // Wrap around for looping
                } else {
                    updateText("", true); // Mark finished after last backspace/wait
                    break; // Exit loop if not looping
                }
            }

            const messageToType = messages[currentMessageIndex];

            // Type the message
            await typeString(messageToType);
            await sleep(holdSpeed);

            // Check if this is the last message and we're not looping
            const isLastMessage = currentMessageIndex === messages.length - 1;
            if (!loop && isLastMessage && !backspaceOnFinish) {
                // Keep the last message displayed and mark as finished
                updateText(messageToType, true);
                break;
            }

            // Backspace the message
            await backspaceString();
            await sleep(waitSpeed);

            // Prepare for next iteration
            currentMessageIndex++;

            // Added check: if only one message and looping, stay at index 0
            if (messages.length === 1 && loop) {
                currentMessageIndex = 0;
            }
        } while (true); // Loop is controlled by break statement or continues indefinitely if loop=true
    };

    // Begin the animation loop (after initial display/backspace)
    await runAnimationLoop();
    // --- End of Modified Logic ---
}

/**
 * Simple sleep utility.
 */
const sleep = (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Increments citation numbers in markdown content to match 1-based source indices.
 * Converts [0] to [1], [1] to [2], etc.
 *
 * @param markdown The markdown content with citations
 * @returns Markdown with incremented citation numbers
 */
const incrementCitationNumbers = (markdown: string): string => {
    // First, handle escaped brackets \[(\d+)\] by removing the backslash
    let cleanedMarkdown = markdown.replace(/\\\[(\d+)\\\]/g, (match, num) => {
        const citationNumber = parseInt(num, 10);
        return `[${citationNumber + 1}]`;
    });
    
    // Then handle normal brackets [(\d+)]
    cleanedMarkdown = cleanedMarkdown.replace(/\[(\d+)\]/g, (match, num) => {
        const citationNumber = parseInt(num, 10);
        return `[${citationNumber + 1}]`;
    });
    
    return cleanedMarkdown;
};

// Dollar delimited math, after normalizeMathDelimiters has rewritten the bracket forms.
const LATEX_MATH = /\$\$([\s\S]*?)\$\$|\$([^$\n]+)\$/g;

/**
 * Rewrites the body of one formula as text. The list holds the commands that models write in
 * answers. Each other command keeps its name and loses its backslash, which is easier to
 * read than the raw command.
 */
const latexBodyToPlainText = (body: string): string =>
    body
        .replace(/\\frac\{([^{}]*)\}\{([^{}]*)\}/g, "$1/$2")
        .replace(/\\(?:text|mathbf|mathrm)\{([^{}]*)\}/g, "$1")
        .replace(/\\times/g, "×")
        .replace(/\\cdot/g, "·")
        .replace(/\\%/g, "%")
        .replace(/\\,/g, " ")
        .replace(/([\^_])\{([^{}]*)\}/g, "$1$2")
        // A group after a digit sets the spacing only, so the text keeps its content.
        // 480{,}000 is the LaTeX form of a thousands separator.
        .replace(/(\d)\{([^{}]*)\}/g, "$1$2")
        .replace(/\\\\/g, " ")
        .replace(/\\([a-zA-Z]+)/g, "$1")
        .replace(/\\(.)/g, "$1");

/**
 * Rewrites the LaTeX in an answer as plain text. KaTeX renders the formulas in the app, but
 * a document that receives the copied answer gets the source. "$420\,000$" must therefore
 * arrive as "420 000". Code keeps its backslashes and dollars, which are content there.
 */
export const latexToPlainText = (markdown: string): string =>
    outsideCode(normalizeMathDelimiters(markdown), (segment) =>
        segment.replace(LATEX_MATH, (_match, display?: string, inline?: string) =>
            latexBodyToPlainText(display ?? inline ?? "")
        )
    );

/**
 * Returns a html string from the markdown content of the answer using remark-gfm. And creates a html list of the sources.
 *
 * @param answer AssistantAnswer
 * @returns html string
 */
export const prettyMessageForClipboard = (
    answer: AssistantAnswer,
    sources: SourceInfo[],
    relatedTools?: RelatedSourceInfo[],
    artifacts?: Artifact[]
) => {
    const markdown = latexToPlainText(incrementCitationNumbers(answer.content));
    const html = remark()
        .use(remarkGfm)
        .use(remarkHtml)
        .processSync(markdown)
        .toString();
    const sourcesHtml = sources
        ?.map((source, index) => {
            return `<li><a href="https://app.dib.no/${
                source.url
            }" target="_blank"><strong>${source.index + 1}. ${
                source.name
            }</strong></a></li>`;
        })
        .join("\n");
    
    // Filter relatedTools to only include those referenced in the message
    const filteredRelatedTools = answer?.content && relatedTools
        ? relatedTools.filter((tool) => answer.content.includes(tool.url))
        : [];
    
    const relatedToolsHtml = filteredRelatedTools.length > 0
        ? `<section><h3>${i18next.t(
              "references.relatedTools.title"
          )}</h3><ul>${filteredRelatedTools
              .map((tool) => {
                  return `<li><a href="https://app.dib.no/${
                      tool.url
                  }" target="_blank"><strong>${tool.title}</strong> - ${
                      tool.description
                  }</a></li>`;
              })
              .join("\n")}</ul></section>`
        : "";
    
    const sourcesSectionHtml = sourcesHtml
        ? `<section><h3>${i18next.t(
              "references.title"
          )}</h3><ul>${sourcesHtml}</ul></section>`
        : "";

    const artifactsHtml = (artifacts ?? [])
        .map(artifactToClipboardHtml)
        .join("");

    return `<div>${html}${sourcesSectionHtml}${relatedToolsHtml}${artifactsHtml}</div>`;
};

/**
 * Returns a plain text string from the markdown content of the answer using remark-parse and strip-markdown.
 *
 * @param answer AssistantAnswer
 * @returns plain text string
 */
export const plainTextForClipboard = (
    answer: AssistantAnswer,
    sources: SourceInfo[],
    relatedTools?: RelatedSourceInfo[],
    artifacts?: Artifact[]
) => {
    const markdown = latexToPlainText(incrementCitationNumbers(answer.content));
    let text = remark().use(stripMarkdown).processSync(markdown).toString();
    
    // Clean up any remaining escaped brackets in the plain text
    text = text.replace(/\\\[(\d+)\\\]/g, '[$1]');
    text = text.replace(/\\\[/g, '[').replace(/\\\]/g, ']');
    const sourcesText = sources
        ?.filter((source) => answer?.citations?.includes(source.index))
        ?.map((source, index) => {
            return `${source.index + 1}. ${source.name}`;
        })
        .join("\n");
    
    // Filter relatedTools to only include those referenced in the message
    const filteredRelatedTools = answer?.content && relatedTools
        ? relatedTools.filter((tool) => answer.content.includes(tool.url))
        : [];
    
    const relatedToolsText = filteredRelatedTools.length > 0
        ? `\n\n${i18next.t("references.relatedTools.title")}\n${filteredRelatedTools
              .map((tool) => `${tool.title} - ${tool.description}`)
              .join("\n")}`
        : "";
    
    const sourcesSectionText = sourcesText
        ? `\n\n${i18next.t("references.title")}\n${sourcesText}`
        : "";

    const artifactsText = (artifacts ?? [])
        .map(artifactToClipboardText)
        .join("");

    return `${text}${sourcesSectionText}${relatedToolsText}${artifactsText}`;
};

// Pricing per 1M tokens
const priceTable: Record<GPTModel, {prompt: number; completion: number}> = {
    "gpt-4o": {
        prompt: 2.5,
        completion: 10.0,
    },
    "gpt-4o-mini": {
        prompt: 0.15,
        completion: 0.6,
    },
};

const nokRate = 11.11;

export const calculateCost = (usageDict?: Record<GPTModel, Usage>) => {
    if (!usageDict) {
        return {
            costUsd: "0.0000",
            costNok: "0.0000",
        };
    }

    const totalCost = Object.keys(usageDict).reduce((acc, curr) => {
        const price = priceTable[curr as GPTModel];
        if (!price || !usageDict[curr as GPTModel]) {
            return acc;
        }
        return (
            acc +
            (price.prompt * usageDict[curr as GPTModel].prompt_tokens) /
                1000000 +
            (price.completion * usageDict[curr as GPTModel].completion_tokens) /
                1000000
        );
    }, 0);

    return {
        costUsd: totalCost.toFixed(4),
        costNok: (totalCost * nokRate).toFixed(4),
    };
};

export function autoCompletePartialJson(json: string): string {
    let completedJson = json.trim();

    // Remove starting and ending markdown code block markers if present
    completedJson = completedJson.replace(/^```\s*|\s*```$/g, "");

    // Stack to track opening brackets/braces
    const stack: string[] = [];
    let inString = false;
    let isEscaped = false;

    // Analyze the string character by character
    for (let i = 0; i < completedJson.length; i++) {
        const char = completedJson[i];

        if (char === "\\") {
            isEscaped = !isEscaped;
            continue;
        }

        if (!isEscaped && char === '"') {
            inString = !inString;
        } else if (!inString && !isEscaped) {
            if (char === "{" || char === "[") {
                stack.push(char);
            } else if (char === "}") {
                if (stack[stack.length - 1] === "{") {
                    stack.pop();
                }
            } else if (char === "]") {
                if (stack[stack.length - 1] === "[") {
                    stack.pop();
                }
            }
        }

        isEscaped = false;
    }

    // Close unclosed strings
    if (inString) {
        completedJson += '"';
    }

    // Close any remaining open structures
    while (stack.length > 0) {
        const opener = stack.pop();
        if (opener === "{") {
            completedJson += "}";
        } else if (opener === "[") {
            completedJson += "]";
        }
    }

    return completedJson;
}

/**
 * For some reason the LLM restarts the JSON object after every stop_reason. So we take the last
 * JSON object and return that.
 * TODO: Fix this backend-side.
 */
export const llmBadJsonToGoodJson = (json: string): AssistantAnswer | null => {
    const completedJson = autoCompletePartialJson(json);
    try {
        return JSON.parse(completedJson) as AssistantAnswer;
        /* const split = completedJson.split('{"thinking');
        const last = `{"thinking${split[split.length - 1]}`;
        return JSON.parse(last) as AssistantAnswer; */
    } catch (error) {
        return null;
    }
};

export const openIntercom = () => {
    if ((window as any).Intercom) {
        (window as any).Intercom("show");
    }
};

export const closeIntercom = () => {
    if ((window as any).Intercom) {
        (window as any).Intercom("hide");
    }
};
